import type { InferGetStaticPropsType, NextPage } from "next";
import Layout from "../components/layout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import TextSubtitle from "../components/text_subtitle";
import { useTranslation } from "next-i18next";
import PopularTheme from "../components/home/popular_theme";
import useSidebarService from "../contexts/useSidebarService";
import React, { useEffect, useState } from "react";
import { NextSeo } from "next-seo";
import ReferenceContainer from "../components/reference_container";
import ReferenceAPIService, {
  ReferenceData,
} from "../services/reference_api_service";
import dayjs from "dayjs";
import { Button } from "antd";

const why_ds_index = [
  {
    title: "why_index_title_1",
    subtitle: "why_index_subtitle_1",
  },
  {
    title: "why_index_title_2",
    subtitle: "why_index_subtitle_2",
  },
  {
    title: "why_index_title_3",
    subtitle: "why_index_subtitle_3",
  },
];

const reference = [
  {
    title: "reference_title_1",
    code: "reference_code_1",
    symbol: "KRX:305720",
    subtitle: "reference_subtitle_1",
  },
  {
    title: "reference_title_2",
    code: "reference_code_2",
    symbol: "KRX:364690",
    subtitle: "reference_subtitle_2",
  },
  {
    title: "reference_title_3",
    code: "reference_code_3",
    symbol: "KRX:388420",
    subtitle: "reference_subtitle_3",
  },
  {
    title: "reference_title_4",
    code: "reference_code_4",
    symbol: "KRX:433500",
    subtitle: "reference_subtitle_4",
  },
];

export interface Theme {
  theme: {
    index: string;
    title_ko: string;
    title_en: string;
    url: string;
  };
  image: ImageType;
}

export interface ImageType {
  alt: string;
  title: string;
  blurDataURL?: string;
  src: string;
  type?: string | undefined;
}

const Home: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = (
  props
) => {
  const { t } = useTranslation("common");
  const [themeList, setthemeList] = useState<Theme[]>([]);
  const { sidebar, loading } = useSidebarService();
  const tech_theme_index = ["27", "28", "7", "9", "18", "21"];
  const life_theme_index = ["5", "8", "10", "14", "16", "17"];
  const bio_theme_index = ["13", "15", "19", "20", "22"];
  const [refData, setrefData] = useState<{ [symbol: string]: ReferenceData }>();

  useEffect(() => {
    function getThemePlaceholder() {
      const themeList = sidebar
        .filter((v) => v.parent_id != 0 && (v?.image_url_s ?? "") != "")
        .map((theme) => {
          return {
            theme: {
              index: theme.index_id,
              title_ko: theme.index_name_ko,
              title_en: theme.index_name_en,
              url: "/indice?index=" + theme.index_id,
            },
            image: {
              src: theme.image_url_s ?? "",
              alt: theme.index_name_en,
              title: theme.index_name_en,
            },
          };
        });

      setthemeList(themeList);
    }
    if (sidebar.length > 0) {
      getThemePlaceholder();
    }
  }, [sidebar]);

  useEffect(() => {
    async function getData() {
      const date = dayjs();
      const last_year = date.subtract(1, "year");
      try {
        const res = await ReferenceAPIService.getReferenceInfo(
          last_year.format("YYYY-MM-DD"),
          date.format("YYYY-MM-DD")
        );
        setrefData(res);
      } catch (e) {
        console.log(e);
      }
    }
    getData();
  }, []);

  return (
    <Layout>
      <NextSeo
        openGraph={{
          ...props.meta_data,
        }}
      />
      <main className="flex flex-col justify-center items-center">
        <div className="max-w-6xl w-10/12 mb-12 md:my-22">
          <h1 className="text-header text-center text-text-primary font-bold">
            DeepSearch AI Index
          </h1>
          <div className="mt-2 text-center whitespace-pre-line text-text-secondary">
            {t("landing_page_subtitle")}
          </div>
          <div className="mt-8 text-center">
            <a
              target="_blank"
              href="https://docs.google.com/forms/d/e/1FAIpQLScrvPoBaZCH-EX9AapP4rccb7kLpXX8dw5bAX4WpLTKyPxR6Q/viewform"
              rel="noreferrer"
            >
              <Button
                type="primary"
                shape="round"
                style={{
                  width: "270px",
                  height: "44px",
                  fontWeight: "bold",
                  fontSize: "16px",
                }}
              >
                {t("ask_question")}
              </Button>
            </a>
          </div>
        </div>
        <div className="max-w-6xl w-10/12">
          <PopularTheme
            title={t("tech_theme")}
            themes={themeList.filter((v) =>
              tech_theme_index.includes(v.theme.index)
            )}
            loading={loading}
          />
        </div>
        <div className="max-w-6xl w-10/12">
          <PopularTheme
            title={t("life_theme")}
            themes={themeList.filter((v) =>
              life_theme_index.includes(v.theme.index)
            )}
            loading={loading}
          />
        </div>
        <div className="max-w-6xl w-10/12">
          <PopularTheme
            title={t("bio_theme")}
            themes={themeList.filter((v) =>
              bio_theme_index.includes(v.theme.index)
            )}
            loading={loading}
          />
        </div>

        <div className="max-w-6xl w-10/12 my-14">
          <h1 className="text-2xl font-bold text-center text-text-primary">
            {t("why_deepsearch")}
          </h1>
          <div className="grid grid-cols-1 my-10 lg:grid-cols-3 gap-2 items-stretch">
            {why_ds_index.map((val, key) => (
              <TextSubtitle
                key={key}
                title={t(val.title)}
                subtitle={t(val.subtitle)}
              />
            ))}
          </div>
        </div>

        <div className="max-w-6xl w-10/12">
          <h1 className="text-2xl font-bold text-center text-text-primary">
            {t("reference")}
          </h1>
          <div className="grid grid-cols-1 my-10 lg:grid-cols-2 gap-2 items-stretch">
            {reference.map((val, key) => (
              <div key={key} className="h-full">
                <a
                  href={`https://deepsearch.com/analytics/company-analysis/${val.symbol}`}
                  target="_parent"
                  rel="noreferrer"
                >
                  <ReferenceContainer
                    code={t(val.code)}
                    title={t(val.title)}
                    stock={
                      refData ? refData[val.symbol]?.stock_price : undefined
                    }
                    market_cap={
                      refData ? refData[val.symbol]?.market_cap : undefined
                    }
                    subtitle={t(val.subtitle)}
                  />
                </a>
              </div>
            ))}
          </div>
        </div>
      </main>
    </Layout>
  );
};

export const getStaticProps = async ({ locale }: any) => {
  return {
    props: {
      meta_data: {
        title:
          (locale == "ko" ? "딥서치 지수" : "DeepSearch Index") +
          " - DeepSearch AI Index | 기업 정보가 필요한 순간, 딥서치",
        description:
          locale == "ko"
            ? "DeepSearch의 강력한 데이터 치리 기술을 이용하여 자신만의 고유한 Index를 구성할 수 있습니다. DeepSearch는 현재 50여개 주제 포트폴리오를 구성하여 공시하고 있습니다."
            : "DeepSearch's powerful data technology allows you to configure your own unique Index. DeepSearch currently organizes and discloses a portfolio of 50 topics.",
      },
      ...(await serverSideTranslations(locale, ["common"])),
    },
  };
};

export default Home;
